import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import {Close as CloseIcon} from '@mui/icons-material';
import {DateTimePicker} from '@mui/x-date-pickers';
import {TASK_PRIORITY_OPTIONS, TASK_STATUS_OPTIONS, TASK_TYPE} from '../../models/TaskDTO';
import {Form, Formik, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import {LeadCrudService, LeadTaskManagementService, TasksService} from '../../api/generated/requests/services.gen';
import {useSnackbar} from 'notistack';
import {useQueryClient} from '@tanstack/react-query';
import {TaskDTO} from "../../api";
import {LeadDTO} from '../../api/generated/requests/types.gen';
import LeadContactDialog from '../Leads/LeadContactDialog';
import TaskDetailDialog from './TaskDetailDialog';
import {getUserFullName, useUserData} from '../../hooks/useUserData';
import UserSearchSelect from '../Common/UserSearchSelect';
import MultiContactSelect from '../Common/MultiContactSelect';


const getValidationSchema = (isEdit: boolean) => {
  const baseSchema: any = {
    title: Yup.string()
      .required('Tytuł jest wymagany')
      .max(255, 'Tytuł nie może przekraczać 255 znaków'),
    description: Yup.string()
      .max(5000, 'Opis nie może przekraczać 5000 znaków'),
    priority: Yup.string().required('Priorytet jest wymagany'),
    taskType: Yup.string().required('Typ zadania jest wymagany'),
    startDate: Yup.date().required('Termin jest wymagany'),
    endDate: Yup.date().when('taskType', {
      is: 'MEETING',
      then: (schema) => schema
        .required('Termin do jest wymagany')
        .min(Yup.ref('startDate'), 'Termin do nie może być wcześniejszy niż termin od'),
      otherwise: (schema) => schema.notRequired()
    }),
  };

  if (!isEdit) {
    // For new tasks, add minimum date validation
    baseSchema.startDate = Yup.date()
      .required('Termin jest wymagany')
  }

  if (isEdit) {
    // For editing, add status validation
    baseSchema.status = Yup.string().required('Status jest wymagany');
  }

  return Yup.object(baseSchema);
};

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  task?: TaskDTO | null;
  onSave: (task: TaskDTO) => void;
  refreshTasks?: () => Promise<void>;
  initialStartDate?: Date;
  initialEndDate?: Date;
  preAssignedLeadId?: number;
}

const TaskDialog: React.FC<TaskDialogProps> = ({ 
  open, 
  onClose, 
  task = null, 
  onSave,
  refreshTasks,
  initialStartDate,
  initialEndDate,
  preAssignedLeadId
}) => {
  const [openLeadDialog, setOpenLeadDialog] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [createNext, setCreateNext] = useState(false);
  const [openTaskDetailDialog, setOpenTaskDetailDialog] = useState(false);
  const [createdTaskId, setCreatedTaskId] = useState<number | null>(null);
  const [formikSetFieldValue, setFormikSetFieldValue] = useState<((field: string, value: any) => void) | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const isEdit = !!task;

  // Automatically set preAssignedLeadId in selectedContacts when dialog opens
  useEffect(() => {
    if (open && preAssignedLeadId && !isEdit) {
      // Only set if dialog is open, preAssignedLeadId is provided, and it's not edit mode
      // Also check if the lead is not already in selectedContacts to avoid duplicates
      if (!selectedContacts.includes(preAssignedLeadId)) {
        setSelectedContacts([preAssignedLeadId]);
      }
    }
  }, [open, preAssignedLeadId, isEdit, selectedContacts]);

  // Refresh contact list when dialog opens to ensure fresh data
  useEffect(() => {
    if (open) {
      // Invalidate the paginated contacts cache to force fresh API call
      queryClient.invalidateQueries({ queryKey: ['lead-contacts-paginated-default'] });
    }
  }, [open, queryClient]);

  // Fetch user data using optimized hook
  const { currentUser, domainUsers, isLoading: loadingUsers } = useUserData();


  // Helper function for getting user display names
  const getUserDisplayNameById = (userId: string | undefined) => {
    if (!userId) {
      return 'Nieprzypisane';
    }
    const user = domainUsers.find(u => u.id === userId);
    if (!user) {
      return 'Nieznany użytkownik';
    }
    return getUserFullName(user);
  };

  // Handler functions for LeadContactDialog
  const handleOpenLeadDialog = () => {
    setOpenLeadDialog(true);
  };

  const handleCloseLeadDialog = () => {
    setOpenLeadDialog(false);
  };

  const handleSaveLead = async (lead: LeadDTO) => {
    setIsSubmittingLead(true);
    try {
      const newLead = await LeadCrudService.postApiLeadContacts({
        requestBody: lead
      });
      enqueueSnackbar(`Kontakt został utworzony (ID: ${newLead.prefixId}-${newLead.id})`, { variant: 'success', autoHideDuration: 15000 });
      handleCloseLeadDialog();

      // Immediately update the cache with the new contact data
      if (newLead.id) {
        // Add to individual contact cache
        queryClient.setQueryData(['LeadCrudServiceGetApiLeadContactsById', { id: newLead.id }], newLead);
        
        // Update the paginated default cache by adding the new contact at the beginning
        queryClient.setQueryData(['lead-contacts-paginated-default'], (oldData: any) => {
          if (oldData && oldData.content) {
            return {
              ...oldData,
              content: [newLead, ...oldData.content]
            };
          }
          return oldData;
        });
      }

      // Automatycznie wybierz nowo utworzony kontakt w MultiContactSelect
      if (newLead.id && !selectedContacts.includes(newLead.id)) {
        setSelectedContacts(prev => [...prev, newLead.id!]);
      }

      // Wyślij globalne zdarzenie do odświeżenia listy leadów
      window.dispatchEvent(new CustomEvent('refresh-leads'));

      return newLead;
    } catch (error) {
      console.error('Błąd podczas tworzenia kontaktu:', error);
      enqueueSnackbar('Wystąpił błąd podczas tworzenia kontaktu', { variant: 'error' });
    } finally {
      setIsSubmittingLead(false);
    }
  };

  // Handler functions for TaskDetailDialog
  const handleCloseTaskDetailDialog = () => {
    setOpenTaskDetailDialog(false);
    setCreatedTaskId(null);
  };

  const handleTaskUpdated = async () => {
    // Wyślij globalne zdarzenie do odświeżenia listy zadań
    window.dispatchEvent(new CustomEvent('refresh-tasks'));
  };

  const handleSubmit = async (values: TaskDTO, { resetForm, setSubmitting }: FormikHelpers<TaskDTO>) => {
    try {
      if (isEdit) {
        // For editing, just call the onSave callback
        onSave(values);
      } else {
        // For creating new task
        const createdTask = await TasksService.postApiTasks({ requestBody: values });
        setCreatedTaskId(createdTask.id || null);

        // Assign contacts to the task if any are selected
        if (selectedContacts.length > 0 && createdTask.id) {
          try {
            await LeadTaskManagementService.postApiLeadTasksAssignLeadsToTask({
              requestBody: {
                taskId: createdTask.id,
                leadIds: selectedContacts,
                assignedByUserId: currentUser?.id
              }
            });
            enqueueSnackbar(`Zadanie zostało utworzone (ID: ${createdTask.id})`, {
              variant: 'success',
              autoHideDuration: 15000,
              action: (snackbarId) => {
                const taskId = createdTask.id; // Capture the specific task ID
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => {
                        if (taskId) {
                          setCreatedTaskId(taskId);
                          setOpenTaskDetailDialog(true);
                        }
                      }}
                    >
                      Otwórz
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="close"
                      color="inherit"
                      onClick={() => closeSnackbar(snackbarId)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              }
            });
          } catch (contactError) {
            console.error('Błąd podczas przypisywania kontaktów:', contactError);
            enqueueSnackbar(`Zadanie zostało utworzone (ID: ${createdTask.id}), ale nie udało się przypisać kontaktów`, {
              variant: 'warning',
              autoHideDuration: 15000,
              action: (snackbarId) => {
                const taskId = createdTask.id; // Capture the specific task ID
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => {
                        if (taskId) {
                          setCreatedTaskId(taskId);
                          setOpenTaskDetailDialog(true);
                        }
                      }}
                    >
                      Otwórz
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="close"
                      color="inherit"
                      onClick={() => closeSnackbar(snackbarId)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              }
            });
          }
        } else {
          enqueueSnackbar(`Zadanie zostało utworzone (ID: ${createdTask.id})`, { 
            variant: 'success',
            autoHideDuration: 15000,
            action: (snackbarId) => {
              const taskId = createdTask.id; // Capture the specific task ID
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => {
                      if (taskId) {
                        setCreatedTaskId(taskId);
                        setOpenTaskDetailDialog(true);
                      }
                    }}
                  >
                    Otwórz
                  </Button>
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={() => closeSnackbar(snackbarId)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            }
          });
        }

        if (createNext) {
          // If "Create Next" is checked, reset form but keep dialog open
          resetForm();
          setSelectedContacts([]);
        } else {
          // If "Create Next" is not checked, reset form and close dialog
          resetForm();
          setSelectedContacts([]);
          onClose();
        }

        // Call the onSave callback to notify parent component
        onSave(createdTask);

        // Dispatch global event to refresh task lists
        window.dispatchEvent(new CustomEvent('refresh-tasks'));
      }
    } catch (error) {
      console.error('Błąd podczas zapisywania zadania:', error);
      enqueueSnackbar('Nie udało się zapisać zadania', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Ustawienie wartości początkowych formularza
  const getInitialValues = (): TaskDTO => {
    if (isEdit && task) {
      return task;
    }

    // For new tasks, use provided initial dates or default to current time
    let startDate: Date;
    let endDate: Date;

    if (initialStartDate) {
      startDate = initialStartDate;
      // For meetings: use provided endDate or +1 hour, for others: +15 minutes
      if (initialEndDate) {
        endDate = initialEndDate;
      } else {
        endDate = new Date(initialStartDate.getTime() + 15 * 60 * 1000); // +15 minutes default
      }
    } else {
      startDate = new Date();
      endDate = new Date();
      endDate.setMinutes(endDate.getMinutes() + 15); // +15 minutes default
    }

    return {
      title: '',
      description: '',
      status: 'TODO', // Status zawsze ustawiony na TODO dla nowych zadań
      priority: 'C',
      taskType: 'EMAIL', // Domyślny typ zadania
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      assignedToUserId: currentUser?.id, // Domyślnie przypisane do aktualnie zalogowanego użytkownika
    };
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
            sx={{
              fontSize: '0.95rem',
              fontWeight: 600,
              py: 1.2,
              px: 2,
            }}
        >
          {isEdit ? 'Edytuj zadanie' : 'Utwórz nowe zadanie'}
        </DialogTitle>

        <Formik
            initialValues={getInitialValues()}
            validationSchema={getValidationSchema(isEdit)}
            onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
            // Store setFieldValue function for use in handleSaveLead
            if (formikSetFieldValue !== setFieldValue) {
              setFormikSetFieldValue(() => setFieldValue);
            }

            return (
              <Form>
                <DialogContent
                    dividers
                    sx={{ backgroundColor: 'background.paper', px: 2, py: 1.5 }}
                >
                  {/* Tytuł */}
                  <TextField
                      fullWidth
                      size="small"
                      margin="none"
                      id="title"
                      name="title"
                      label="Tytuł zadania"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.title && Boolean(errors.title)}
                      helperText={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{touched.title && errors.title}</span>
                          <span style={{
                            color: (values.title?.length || 0) >= 255 ? '#d32f2f' : '#666',
                            fontSize: '0.75rem'
                          }}>
                  {values.title?.length || 0}/255
                </span>
                        </Box>
                      }
                      required
                  />

                  {/* Opis */}
                  <TextField
                      fullWidth
                      size="small"
                      margin="none"
                      id="description"
                      name="description"
                      label="Opis zadania"
                      multiline
                      rows={3}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{touched.description && errors.description}</span>
                          <span style={{
                            color: (values.description?.length || 0) >= 5000 ? '#d32f2f' : '#666',
                            fontSize: '0.75rem'
                          }}>
                  {values.description?.length || 0}/5000
                </span>
                        </Box>
                      }
                  />

                  {/* Status, Priorytet, Typ */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    {isEdit && (
                        <FormControl
                            size="small"
                            sx={{ flex: 1 }}
                            margin="dense"
                            error={touched.status && Boolean(errors.status)}
                        >
                          <InputLabel id="status-label">Status</InputLabel>
                          <Select
                              labelId="status-label"
                              id="status"
                              name="status"
                              value={values.status}
                              label="Status"
                              onChange={handleChange}
                              onBlur={handleBlur}
                          >
                            {TASK_STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                    )}

                    <FormControl
                        size="small"
                        sx={{ flex: 1 }}
                        margin="dense"
                        error={touched.priority && Boolean(errors.priority)}
                    >
                      <InputLabel id="priority-label">Priorytet</InputLabel>
                      <Select
                          labelId="priority-label"
                          id="priority"
                          name="priority"
                          value={values.priority}
                          label="Priorytet"
                          onChange={handleChange}
                          onBlur={handleBlur}
                      >
                        {TASK_PRIORITY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        sx={{ flex: 1 }}
                        margin="dense"
                        error={touched.taskType && Boolean(errors.taskType)}
                    >
                      <InputLabel id="taskType-label">Typ zadania</InputLabel>
                      <Select
                          labelId="taskType-label"
                          id="taskType"
                          name="taskType"
                          value={values.taskType}
                          label="Typ zadania"
                          onChange={handleChange}
                          onBlur={handleBlur}
                      >
                        {TASK_TYPE.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Daty */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    {values.taskType === 'MEETING' ? (
                      <>
                        <FormControl size="small" fullWidth error={touched.startDate && Boolean(errors.startDate)}>
                          <DateTimePicker
                              label="Termin od"
                              value={values.startDate ? new Date(values.startDate) : new Date()}
                              onChange={(newValue) => {
                                if (newValue) {
                                  setFieldValue('startDate', newValue.toISOString());
                                  // For meetings, automatically set end date to +1 hour from start date
                                  const endDate = new Date(newValue);
                                  endDate.setHours(endDate.getHours() + 1);
                                  setFieldValue('endDate', endDate.toISOString());
                                }
                              }}
                              slotProps={{ textField: { size: 'small' } }}
                          />
                        </FormControl>

                        <FormControl size="small" fullWidth error={touched.endDate && Boolean(errors.endDate)}>
                          <DateTimePicker
                              label="Termin do"
                              value={values.endDate ? new Date(values.endDate) : new Date()}
                              onChange={(newValue) => {
                                if (newValue) {
                                  setFieldValue('endDate', newValue.toISOString());
                                }
                              }}
                              slotProps={{ textField: { size: 'small' } }}
                          />
                        </FormControl>
                      </>
                    ) : (
                      <FormControl size="small" fullWidth error={touched.startDate && Boolean(errors.startDate)}>
                        <DateTimePicker
                            label="Termin"
                            value={values.startDate ? new Date(values.startDate) : new Date()}
                            onChange={(newValue) => {
                              if (newValue) {
                                setFieldValue('startDate', newValue.toISOString());
                                // For non-meeting types, automatically set end date to +15 minutes from start date
                                const endDate = new Date(newValue);
                                endDate.setMinutes(endDate.getMinutes() + 15);
                                setFieldValue('endDate', endDate.toISOString());
                              }
                            }}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                      </FormControl>
                    )}
                  </Box>

                  {/* Użytkownik */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <UserSearchSelect
                      value={values.assignedToUserId}
                      onChange={(value) => setFieldValue('assignedToUserId', value)}
                      onBlur={() => handleBlur('assignedToUserId')}
                      users={domainUsers}
                      isLoading={loadingUsers}
                      label="Przypisane do"
                      helperText={loadingUsers ? 'Ładowanie użytkowników...' : 'Wybierz użytkownika, do którego zostanie przypisane zadanie'}
                      size="small"
                      fullWidth
                      getUserDisplayName={getUserDisplayNameById}
                    />
                  </Box>

                  {/* Kontakty */}
                  <Box sx={{ mt: 1 }}>
                    <MultiContactSelect
                      value={selectedContacts}
                      onChange={setSelectedContacts}
                      label="Przypisane kontakty"
                      helperText="Wybierz kontakty, które będą przypisane do tego zadania"
                      size="small"
                      fullWidth
                      onAddNewContact={handleOpenLeadDialog}
                      parentDialogOpen={open}
                    />
                  </Box>
                </DialogContent>

                {/* Przyciski akcji */}
                <DialogActions sx={{ px: 2, py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, width: '100%' }}>
                    {!isEdit && (
                        <FormControlLabel
                            control={
                              <Checkbox
                                  checked={createNext}
                                  onChange={(e) => setCreateNext(e.target.checked)}
                                  color="primary"
                                  size="small"
                              />
                            }
                            label="Utwórz następne"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button onClick={onClose} sx={{ fontSize: '0.8rem', py: 0.5, px: 1.5 }}>Anuluj</Button>
                      <Button type="submit" variant="contained" color="primary" sx={{ fontSize: '0.8rem', py: 0.5, px: 1.5 }}>
                        {isEdit ? 'Zapisz zmiany' : 'Utwórz zadanie'}
                      </Button>
                    </Box>
                  </Box>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      <LeadContactDialog
        open={openLeadDialog}
        onClose={handleCloseLeadDialog}
        lead={null}
        onSave={handleSaveLead}
        isSubmitting={isSubmittingLead}
      />

      <TaskDetailDialog
        open={openTaskDetailDialog}
        taskId={createdTaskId}
        onClose={handleCloseTaskDetailDialog}
        onTaskUpdated={handleTaskUpdated}
      />
    </>
  );
};

export default TaskDialog;
